import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, 
  Shield, 
  Upload, 
  Download, 
  Share2, 
  Trash2, 
  Eye, 
  Edit, 
  Users, 
  Clock, 
  Tag, 
  AlertTriangle,
  Lock,
  CheckCircle,
  X,
  Search,
  Filter,
  Plus
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { 
  getSecureDocuments, 
  uploadSecureDocument, 
  getSecureDocument,
  downloadSecureDocument,
  shareDocument,
  revokeDocumentAccess,
  getDocumentPermissions,
  getDocumentAccessLogs,
  deleteSecureDocument,
  SecureDocument,
  DocumentPermission
} from '../../lib/secureDocuments';
import Card from '../atoms/Card';
import Button from '../atoms/Button';
import Input from '../atoms/Input';
import { DocumentCardSkeleton } from '../atoms/SkeletonLoader';
import toast from 'react-hot-toast';

const SecureDocumentManager: React.FC = () => {
  const { user } = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);
  const [documents, setDocuments] = useState<{
    owned: SecureDocument[];
    shared: SecureDocument[];
  }>({ owned: [], shared: [] });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showAccessLogsModal, setShowAccessLogsModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<SecureDocument | null>(null);
  const [permissions, setPermissions] = useState<DocumentPermission[]>([]);
  const [accessLogs, setAccessLogs] = useState<any[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadFormData, setUploadFormData] = useState({
    name: '',
    description: '',
    tags: ''
  });
  const [shareFormData, setShareFormData] = useState({
    email: '',
    permissionLevel: 'read' as 'read' | 'write' | 'admin',
    expiresAt: ''
  });

  // Load documents on component mount
  useEffect(() => {
    loadDocuments();
  }, []);

  // Load documents from API
  const loadDocuments = async () => {
    setIsLoading(true);
    try {
      const result = await getSecureDocuments();
      if (result.error) {
        toast.error(result.error);
      } else {
        setDocuments({
          owned: result.owned || [],
          shared: result.shared || []
        });
      }
    } catch (error: any) {
      toast.error(`Failed to load documents: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter documents based on search query and selected tags
  const filteredDocuments = {
    owned: documents.owned.filter(doc => {
      const matchesSearch = searchQuery === '' || 
        doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (doc.description || '').toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesTags = selectedTags.length === 0 || 
        selectedTags.every(tag => doc.tags.includes(tag));
      
      return matchesSearch && matchesTags;
    }),
    shared: documents.shared.filter(doc => {
      const matchesSearch = searchQuery === '' || 
        doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (doc.description || '').toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesTags = selectedTags.length === 0 || 
        selectedTags.every(tag => doc.tags.includes(tag));
      
      return matchesSearch && matchesTags;
    })
  };

  // Get all unique tags from documents
  const allTags = Array.from(new Set([
    ...documents.owned.flatMap(doc => doc.tags),
    ...documents.shared.flatMap(doc => doc.tags)
  ])).sort();

  // Handle file selection for upload
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      setUploadFile(file);
      setUploadFormData(prev => ({
        ...prev,
        name: file.name
      }));
    }
  };

  // Handle upload form submission
  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadFile) {
      toast.error('Please select a file to upload');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          const newProgress = prev + Math.random() * 10;
          return newProgress > 95 ? 95 : newProgress;
        });
      }, 300);

      // Process tags
      const tags = uploadFormData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(Boolean);

      // Upload document
      const result = await uploadSecureDocument(
        uploadFile,
        uploadFormData.name,
        uploadFormData.description,
        tags
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (result.success) {
        toast.success('Document uploaded successfully');
        setShowUploadModal(false);
        loadDocuments();
      } else {
        toast.error(result.error || 'Upload failed');
      }
    } catch (error: any) {
      toast.error(`Upload failed: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  // Handle document download
  const handleDownload = async (document: SecureDocument) => {
    setIsDownloading(true);
    try {
      const result = await downloadSecureDocument(document.id);
      
      if (result.success && result.data) {
        // Create download link
        const blob = new Blob([result.data], { type: document.fileType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = document.name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        toast.success('Document downloaded successfully');
      } else {
        toast.error(result.error || 'Download failed');
      }
    } catch (error: any) {
      toast.error(`Download failed: ${error.message}`);
    } finally {
      setIsDownloading(false);
    }
  };

  // Handle document sharing
  const handleShare = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDocument) return;
    
    setIsSharing(true);
    try {
      const expiresAt = shareFormData.expiresAt 
        ? new Date(shareFormData.expiresAt) 
        : undefined;
        
      const result = await shareDocument(
        selectedDocument.id,
        shareFormData.email,
        shareFormData.permissionLevel,
        expiresAt
      );
      
      if (result.success) {
        toast.success(`Document shared with ${shareFormData.email}`);
        setShowShareModal(false);
        loadDocuments();
      } else {
        toast.error(result.error || 'Failed to share document');
      }
    } catch (error: any) {
      toast.error(`Sharing failed: ${error.message}`);
    } finally {
      setIsSharing(false);
    }
  };

  // Load document permissions
  const loadPermissions = async (document: SecureDocument) => {
    try {
      const result = await getDocumentPermissions(document.id);
      if (result.error) {
        toast.error(result.error);
      } else {
        setPermissions(result.permissions || []);
        setSelectedDocument(document);
        setShowPermissionsModal(true);
      }
    } catch (error: any) {
      toast.error(`Failed to load permissions: ${error.message}`);
    }
  };

  // Load document access logs
  const loadAccessLogs = async (document: SecureDocument) => {
    try {
      const result = await getDocumentAccessLogs(document.id);
      if (result.error) {
        toast.error(result.error);
      } else {
        setAccessLogs(result.logs || []);
        setSelectedDocument(document);
        setShowAccessLogsModal(true);
      }
    } catch (error: any) {
      toast.error(`Failed to load access logs: ${error.message}`);
    }
  };

  // Handle permission revocation
  const handleRevokePermission = async (permission: DocumentPermission) => {
    try {
      const result = await revokeDocumentAccess(
        permission.documentId,
        permission.grantedTo
      );
      
      if (result.success) {
        toast.success('Access revoked successfully');
        // Refresh permissions
        if (selectedDocument) {
          loadPermissions(selectedDocument);
        }
        loadDocuments();
      } else {
        toast.error(result.error || 'Failed to revoke access');
      }
    } catch (error: any) {
      toast.error(`Failed to revoke access: ${error.message}`);
    }
  };

  // Handle document deletion
  const handleDeleteDocument = async (document: SecureDocument) => {
    if (!confirm(`Are you sure you want to delete "${document.name}"? This action cannot be undone.`)) {
      return;
    }
    
    setIsDeleting(true);
    try {
      const result = await deleteSecureDocument(document.id);
      
      if (result.success) {
        toast.success('Document deleted successfully');
        loadDocuments();
      } else {
        toast.error(result.error || 'Failed to delete document');
      }
    } catch (error: any) {
      toast.error(`Deletion failed: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Document Card Component
  const DocumentCard: React.FC<{
    document: SecureDocument;
    isOwned: boolean;
  }> = ({ document, isOwned }) => {
    return (
      <Card className="p-4 h-full flex flex-col">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center mr-3">
              <FileText className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900 line-clamp-1" title={document.name}>
                {document.name}
              </h3>
              <p className="text-sm text-gray-500">
                {formatFileSize(document.fileSize)}
              </p>
            </div>
          </div>
          {document.isShared && (
            <div className="flex items-center bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
              <Users className="h-3 w-3 mr-1" />
              Shared
            </div>
          )}
        </div>
        
        {document.description && (
          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
            {document.description}
          </p>
        )}
        
        <div className="flex flex-wrap gap-1 mb-3">
          {document.tags.map((tag, index) => (
            <span 
              key={index}
              className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded"
            >
              {tag}
            </span>
          ))}
        </div>
        
        <div className="text-xs text-gray-500 mb-3">
          <div className="flex items-center">
            <Clock className="h-3 w-3 mr-1" />
            Updated {formatDate(document.updatedAt)}
          </div>
          {document.lastAccessedAt && (
            <div className="flex items-center mt-1">
              <Eye className="h-3 w-3 mr-1" />
              Accessed {formatDate(document.lastAccessedAt)}
            </div>
          )}
        </div>
        
        <div className="mt-auto pt-3 border-t border-gray-100">
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              icon={Download}
              onClick={() => handleDownload(document)}
              disabled={isDownloading}
              className="flex-1"
            >
              Download
            </Button>
            
            {isOwned && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  icon={Share2}
                  onClick={() => {
                    setSelectedDocument(document);
                    setShareFormData({
                      email: '',
                      permissionLevel: 'read',
                      expiresAt: ''
                    });
                    setShowShareModal(true);
                  }}
                  className="flex-1"
                >
                  Share
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  icon={Users}
                  onClick={() => loadPermissions(document)}
                  className="flex-1"
                >
                  Permissions
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  icon={Eye}
                  onClick={() => loadAccessLogs(document)}
                  className="flex-1"
                >
                  Access Logs
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  icon={Trash2}
                  onClick={() => handleDeleteDocument(document)}
                  disabled={isDeleting}
                  className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  Delete
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Shield className="h-6 w-6 mr-2 text-primary-600" />
            Secure Document Manager
          </h1>
          <p className="text-gray-600">
            End-to-end encrypted document storage with granular access control
          </p>
        </div>
        
        <Button
          variant="primary"
          icon={Upload}
          onClick={() => setShowUploadModal(true)}
        >
          Upload Document
        </Button>
      </div>
      
      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Input
            type="text"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            icon={Search}
            className="w-full"
          />
        </div>
        
        <div className="flex gap-2 overflow-x-auto pb-2 max-w-full">
          {allTags.map(tag => (
            <Button
              key={tag}
              variant={selectedTags.includes(tag) ? "primary" : "outline"}
              size="sm"
              icon={Tag}
              onClick={() => {
                if (selectedTags.includes(tag)) {
                  setSelectedTags(selectedTags.filter(t => t !== tag));
                } else {
                  setSelectedTags([...selectedTags, tag]);
                }
              }}
            >
              {tag}
            </Button>
          ))}
          
          {selectedTags.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              icon={X}
              onClick={() => setSelectedTags([])}
            >
              Clear
            </Button>
          )}
        </div>
      </div>
      
      {/* Documents */}
      <div className="space-y-8">
        {/* Owned Documents */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            My Documents
          </h2>
          
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <DocumentCardSkeleton key={i} />
              ))}
            </div>
          ) : filteredDocuments.owned.length === 0 ? (
            <Card className="p-6 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">
                No documents found
              </h3>
              <p className="text-gray-600 mb-4">
                {searchQuery || selectedTags.length > 0
                  ? "No documents match your search criteria"
                  : "Upload your first document to get started"}
              </p>
              <Button
                variant="primary"
                icon={Upload}
                onClick={() => setShowUploadModal(true)}
              >
                Upload Document
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredDocuments.owned.map(document => (
                <DocumentCard
                  key={document.id}
                  document={document}
                  isOwned={true}
                />
              ))}
            </div>
          )}
        </div>
        
        {/* Shared Documents */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Shared With Me
          </h2>
          
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2].map(i => (
                <DocumentCardSkeleton key={i} />
              ))}
            </div>
          ) : filteredDocuments.shared.length === 0 ? (
            <Card className="p-6 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">
                No shared documents
              </h3>
              <p className="text-gray-600">
                Documents shared with you will appear here
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredDocuments.shared.map(document => (
                <DocumentCard
                  key={document.id}
                  document={document}
                  isOwned={false}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  Upload Secure Document
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={X}
                  onClick={() => setShowUploadModal(false)}
                  disabled={isUploading}
                />
              </div>
              
              <form onSubmit={handleUpload} className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    onChange={handleFileSelect}
                    disabled={isUploading}
                  />
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer block"
                  >
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Upload className="h-6 w-6 text-primary-600" />
                    </div>
                    <p className="text-gray-900 font-medium mb-1">
                      {uploadFile ? uploadFile.name : 'Click to select a file'}
                    </p>
                    <p className="text-gray-500 text-sm">
                      {uploadFile 
                        ? `${formatFileSize(uploadFile.size)} - ${uploadFile.type}` 
                        : 'PDF, Word, Excel, Text, CSV, JPEG, PNG, GIF'}
                    </p>
                  </label>
                </div>
                
                <Input
                  label="Document Name"
                  type="text"
                  value={uploadFormData.name}
                  onChange={(e) => setUploadFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  disabled={isUploading}
                />
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description (Optional)
                  </label>
                  <textarea
                    value={uploadFormData.description}
                    onChange={(e) => setUploadFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm
                      placeholder-gray-400 transition-all duration-200
                      focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-20"
                    rows={3}
                    disabled={isUploading}
                  />
                </div>
                
                <Input
                  label="Tags (comma separated)"
                  type="text"
                  value={uploadFormData.tags}
                  onChange={(e) => setUploadFormData(prev => ({ ...prev, tags: e.target.value }))}
                  placeholder="e.g. important, contract, finance"
                  disabled={isUploading}
                />
                
                {isUploading && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Uploading...</span>
                      <span>{Math.round(uploadProgress)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary-500 h-2 rounded-full"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}
                
                <div className="flex items-center pt-2">
                  <Shield className="h-4 w-4 text-primary-600 mr-2" />
                  <span className="text-sm text-gray-600">
                    Files are encrypted before upload
                  </span>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    type="button"
                    onClick={() => setShowUploadModal(false)}
                    disabled={isUploading}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isUploading || !uploadFile || !uploadFormData.name}
                    className="flex-1"
                  >
                    {isUploading ? 'Uploading...' : 'Upload'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
      {/* Share Modal */}
      {showShareModal && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  Share Document
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={X}
                  onClick={() => setShowShareModal(false)}
                  disabled={isSharing}
                />
              </div>
              
              <div className="mb-4">
                <h3 className="font-medium text-gray-900">
                  {selectedDocument.name}
                </h3>
                <p className="text-sm text-gray-600">
                  {selectedDocument.description || 'No description'}
                </p>
              </div>
              
              <form onSubmit={handleShare} className="space-y-4">
                <Input
                  label="Email Address"
                  type="email"
                  value={shareFormData.email}
                  onChange={(e) => setShareFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="Enter recipient's email"
                  required
                  disabled={isSharing}
                />
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Permission Level
                  </label>
                  <select
                    value={shareFormData.permissionLevel}
                    onChange={(e) => setShareFormData(prev => ({ 
                      ...prev, 
                      permissionLevel: e.target.value as 'read' | 'write' | 'admin' 
                    }))}
                    className="w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm
                      focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-20"
                    disabled={isSharing}
                  >
                    <option value="read">Read Only</option>
                    <option value="write">Read & Write</option>
                    <option value="admin">Admin (Full Access)</option>
                  </select>
                </div>
                
                <Input
                  label="Expires (Optional)"
                  type="datetime-local"
                  value={shareFormData.expiresAt}
                  onChange={(e) => setShareFormData(prev => ({ ...prev, expiresAt: e.target.value }))}
                  disabled={isSharing}
                />
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 mr-2" />
                    <div className="text-sm text-yellow-800">
                      <p className="font-medium mb-1">Permission Details</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li><strong>Read Only:</strong> Can view and download</li>
                        <li><strong>Read & Write:</strong> Can view, download, and edit</li>
                        <li><strong>Admin:</strong> Can view, download, edit, and manage permissions</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    type="button"
                    onClick={() => setShowShareModal(false)}
                    disabled={isSharing}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isSharing || !shareFormData.email}
                    className="flex-1"
                  >
                    {isSharing ? 'Sharing...' : 'Share'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
      {/* Permissions Modal */}
      {showPermissionsModal && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  Document Permissions
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={X}
                  onClick={() => setShowPermissionsModal(false)}
                />
              </div>
              
              <div className="mb-4">
                <h3 className="font-medium text-gray-900">
                  {selectedDocument.name}
                </h3>
                <p className="text-sm text-gray-600">
                  {selectedDocument.description || 'No description'}
                </p>
              </div>
              
              <div className="mb-4 flex justify-between items-center">
                <h3 className="font-medium text-gray-900">
                  Access Control List
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  icon={Plus}
                  onClick={() => {
                    setShareFormData({
                      email: '',
                      permissionLevel: 'read',
                      expiresAt: ''
                    });
                    setShowPermissionsModal(false);
                    setShowShareModal(true);
                  }}
                >
                  Add User
                </Button>
              </div>
              
              {permissions.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-1">
                    No shared access
                  </h3>
                  <p className="text-gray-600 mb-4">
                    This document hasn't been shared with anyone yet
                  </p>
                  <Button
                    variant="primary"
                    icon={Share2}
                    onClick={() => {
                      setShareFormData({
                        email: '',
                        permissionLevel: 'read',
                        expiresAt: ''
                      });
                      setShowPermissionsModal(false);
                      setShowShareModal(true);
                    }}
                  >
                    Share Document
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Permission
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Granted
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Expires
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {permissions.map(permission => (
                        <tr key={permission.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center">
                                <User className="h-4 w-4 text-gray-500" />
                              </div>
                              <div className="ml-3">
                                <div className="text-sm font-medium text-gray-900">
                                  {permission.grantedToName || 'Unknown User'}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {permission.grantedToEmail}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              permission.permissionLevel === 'read' 
                                ? 'bg-blue-100 text-blue-800' 
                                : permission.permissionLevel === 'write'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-purple-100 text-purple-800'
                            }`}>
                              {permission.permissionLevel === 'read' 
                                ? 'Read Only' 
                                : permission.permissionLevel === 'write'
                                ? 'Read & Write'
                                : 'Admin'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(permission.grantedAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {permission.expiresAt 
                              ? formatDate(permission.expiresAt)
                              : 'Never'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {permission.isActive ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                <X className="h-3 w-3 mr-1" />
                                Revoked
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            {permission.isActive && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRevokePermission(permission)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Revoke
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Access Logs Modal */}
      {showAccessLogsModal && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  Access Logs
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={X}
                  onClick={() => setShowAccessLogsModal(false)}
                />
              </div>
              
              <div className="mb-4">
                <h3 className="font-medium text-gray-900">
                  {selectedDocument.name}
                </h3>
                <p className="text-sm text-gray-600">
                  {selectedDocument.description || 'No description'}
                </p>
              </div>
              
              {accessLogs.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Eye className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-1">
                    No access logs
                  </h3>
                  <p className="text-gray-600">
                    No one has accessed this document yet
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Action
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Timestamp
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          IP Address
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {accessLogs.map(log => (
                        <tr key={log.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center">
                                <User className="h-4 w-4 text-gray-500" />
                              </div>
                              <div className="ml-3">
                                <div className="text-sm font-medium text-gray-900">
                                  {log.users?.name || 'Unknown User'}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {log.users?.email}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              log.access_type === 'view' 
                                ? 'bg-blue-100 text-blue-800' 
                                : log.access_type === 'download'
                                ? 'bg-green-100 text-green-800'
                                : log.access_type === 'edit'
                                ? 'bg-yellow-100 text-yellow-800'
                                : log.access_type === 'share'
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {log.access_type.charAt(0).toUpperCase() + log.access_type.slice(1)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              log.access_status === 'success' 
                                ? 'bg-green-100 text-green-800' 
                                : log.access_status === 'denied'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {log.access_status.charAt(0).toUpperCase() + log.access_status.slice(1)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(new Date(log.created_at))}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {log.ip_address || 'Unknown'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecureDocumentManager;